import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { clinicsApi, servicesApi, appointmentsApi, triageApi, waitTimeApi } from '../services/api/endpoints';
import { AppointmentCreate } from '../types/api';

export const queryKeys = {
  clinics: ['clinics'] as const,
  clinicsMap: (island?: string) => ['clinics', 'map', island ?? 'all'] as const,
  clinicsByIsland: (island: string, page: number) => ['clinics', 'island', island, page] as const,
  clinicsAll: (page: number) => ['clinics', 'all', page] as const,
  clinicsNearby: (lat: number, lng: number, page: number) => ['clinics', 'nearby', lat, lng, page] as const,
  clinic: (slug: string) => ['clinic', slug] as const,
  islands: ['islands'] as const,
  services: ['services'] as const,
  servicesGrouped: ['services', 'grouped'] as const,
  appointment: (token: string) => ['appointment', token] as const,
  waitTime: (clinicId: string) => ['waitTime', clinicId] as const,
};

// Clinic hooks — all clinics for the map (unpaginated)
export const useAllClinics = (island?: string) => useQuery({
  queryKey: queryKeys.clinicsMap(island),
  queryFn: async () => {
    const res = await clinicsApi.getAll(island, 1, 200);
    return res.items;
  },
});

export const useAllNearbyClinics = (lat: number, lng: number, radius?: number, serviceId?: string) => useQuery({
  queryKey: ['clinics', 'nearby', 'map', lat, lng, serviceId] as const,
  queryFn: async () => {
    const res = await clinicsApi.getNearby(lat, lng, radius, serviceId || undefined, 1, 200);
    return res.items;
  },
  enabled: !!lat && !!lng,
});

export const useClinics = (island?: string, page = 1, pageSize = 12) => useQuery({
  queryKey: island ? queryKeys.clinicsByIsland(island, page) : queryKeys.clinicsAll(page),
  queryFn: () => clinicsApi.getAll(island, page, pageSize),
});

export const useNearbyClinics = (lat: number, lng: number, radius?: number, serviceId?: string, page = 1, pageSize = 12) => useQuery({
  queryKey: [...queryKeys.clinicsNearby(lat, lng, page), serviceId] as const,
  queryFn: () => clinicsApi.getNearby(lat, lng, radius, serviceId || undefined, page, pageSize),
  enabled: !!lat && !!lng,
});

export const useClinic = (slug: string) => useQuery({
  queryKey: queryKeys.clinic(slug),
  queryFn: () => clinicsApi.getBySlug(slug),
  enabled: !!slug,
});

export const useIslands = () => useQuery({
  queryKey: queryKeys.islands,
  queryFn: clinicsApi.getIslands,
});

// Service hooks
export const useServices = () => useQuery({
  queryKey: queryKeys.services,
  queryFn: servicesApi.getAll,
});

export const useServicesGrouped = () => useQuery({
  queryKey: queryKeys.servicesGrouped,
  queryFn: servicesApi.getGrouped,
});

// Appointment hooks
export const useCreateAppointment = () => useMutation({
  mutationFn: (data: AppointmentCreate) => appointmentsApi.create(data),
});

export const useAppointmentStatus = (token: string) => useQuery({
  queryKey: queryKeys.appointment(token),
  queryFn: () => appointmentsApi.getStatus(token),
  enabled: !!token,
  retry: false,
});

export const useCancelAppointment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (token: string) => appointmentsApi.cancel(token),
    onSuccess: (_, token) => qc.invalidateQueries({ queryKey: queryKeys.appointment(token) }),
  });
};

// Triage hooks
export const useTriageAssessment = () => useMutation({
  mutationFn: (symptoms: string) => triageApi.assess(symptoms),
});

// Wait time hooks
export const useWaitTime = (clinicId: string) => useQuery({
  queryKey: queryKeys.waitTime(clinicId),
  queryFn: () => waitTimeApi.get(clinicId),
  enabled: !!clinicId,
  refetchInterval: 60000,
});
